import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Building2, Loader2, Search } from 'lucide-react';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { brokersAPI, contractsAPI } from 'services/swagger-api';
import { Checkbox } from 'components/forms/fragments/Checkbox';

const PAGE_SIZE = 100;

interface BrokerItem {
  id: string;
  name: string;
  email?: string;
}

export interface BrokersSelectorHandle {
  /** Sauvegarde la sélection (diff add/remove). Throw si erreur. */
  save: () => Promise<void>;
}

interface BrokersSelectorProps {
  groupContractId: string;
  /** Notifie le parent quand la validité change. Toujours valide ici (0 courtier accepté). */
  onValidityChange?: (isValid: boolean) => void;
}

/**
 * Checklist des courtiers de l'OU autorisés à vendre le contrat collectif.
 * Tous présélectionnés à la création (contrat de portée Plateforme) — on décoche
 * ce qu'on veut exclure.
 */
export const BrokersSelector = forwardRef<BrokersSelectorHandle, BrokersSelectorProps>(
  ({ groupContractId, onValidityChange }, ref) => {
    const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);

    const [allBrokers, setAllBrokers] = useState<BrokerItem[]>([]);
    const [persistedBrokerIds, setPersistedBrokerIds] = useState<string[]>([]);
    const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        setLoading(true);
        try {
          const contract = await contractsAPI.getGroupContractByID({ groupContractId });
          if (cancelled) return;

          const persisted = (contract.brokers ?? [])
            .filter((b) => b.isActive)
            .map((b) => b.brokerId);
          setPersistedBrokerIds(persisted);

          // Charge tous les courtiers de l'OU en paginant (pageSize backend ≤ 100).
          let items: BrokerItem[] = [];
          if (contract.plateformeId) {
            let page = 1;
            let total = Number.POSITIVE_INFINITY;
            while (items.length < total) {
              const response = await brokersAPI.getBrokersPlateforme({
                getGenericListCommandDto: {
                  pageNumber: page,
                  pageSize: PAGE_SIZE,
                  searchString: '',
                  filters: { plateformeIds: [contract.plateformeId] },
                },
              });
              if (cancelled) return;
              const batch = (response.items ?? []).map((b) => ({
                id: b.brokerId,
                name: b.label,
                email: b.email,
              }));
              items = items.concat(batch);
              total = response.totalCount ?? items.length;
              if (batch.length === 0) break;
              page += 1;
            }
          }
          setAllBrokers(items);

          // Présélection : aucun courtier encore enregistré → on coche tout par défaut.
          setCheckedIds(
            persisted.length > 0
              ? new Set(persisted)
              : new Set(items.map((b) => b.id)),
          );
        } catch (err) {
          console.error('Failed to load brokers selector', err);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [groupContractId]);

    useEffect(() => {
      onValidityChange?.(true);
    }, [onValidityChange]);

    const toggle = useCallback((id: string) => {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);

    const filtered = useMemo(() => {
      const s = searchText.trim().toLowerCase();
      if (!s) return allBrokers;
      return allBrokers.filter(
        (b) =>
          b.name.toLowerCase().includes(s)
          || (b.email?.toLowerCase().includes(s) ?? false),
      );
    }, [allBrokers, searchText]);

    const allChecked = allBrokers.length > 0 && allBrokers.every((b) => checkedIds.has(b.id));
    const someChecked = allBrokers.some((b) => checkedIds.has(b.id));

    const toggleAll = useCallback(() => {
      setCheckedIds(allChecked ? new Set() : new Set(allBrokers.map((b) => b.id)));
    }, [allChecked, allBrokers]);

    useImperativeHandle(
      ref,
      () => ({
        async save() {
          const persistedSet = new Set(persistedBrokerIds);
          const toAdd = Array.from(checkedIds).filter((id) => !persistedSet.has(id));
          const toRemove = persistedBrokerIds.filter((id) => !checkedIds.has(id));

          for (const id of toAdd) {
            await contractsAPI.addBrokerToGroupContract({
              groupContractId,
              addBrokerToGroupContractCommand: { brokerId: id },
            });
          }
          for (const id of toRemove) {
            await contractsAPI.removeBrokerFromGroupContract({ groupContractId, brokerId: id });
          }
        },
      }),
      [groupContractId, persistedBrokerIds, checkedIds],
    );

    if (loading) {
      return (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
          <Loader2 className="animate-spin" size={16} />
          {t('GROUP_CONTRACT_PROCESS.BROKERS_STEP.SEARCHING')}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('GROUP_CONTRACT_PROCESS.BROKERS_STEP.PLACEHOLDER')}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            {t('GROUP_CONTRACT_PROCESS.BROKERS_STEP.SELECTED_COUNT', {
              count: checkedIds.size,
            })}
          </span>
          {allBrokers.length > 0 && (
            <button
              type="button"
              onClick={toggleAll}
              className="inline-flex items-center gap-2 text-xs font-medium text-primary hover:text-primary/80"
            >
              {/* Case présentationnelle : c'est le onClick du bouton qui pilote (évite le
                  double déclenchement onChange + onClick qui annulait l'action sur Edge). */}
              <Checkbox
                checked={allChecked}
                indeterminate={someChecked && !allChecked}
                onChange={() => {}}
              />
              {allChecked ? 'Tout décocher' : 'Tout cocher'}
            </button>
          )}
        </div>

        {allBrokers.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-8 border border-dashed border-gray-200 rounded-lg">
            {t('GROUP_CONTRACT_PROCESS.BROKERS_STEP.EMPTY')}
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
            {filtered.length === 0 && (
              <div className="text-sm text-gray-500 text-center py-6">
                {t('GROUP_CONTRACT_PROCESS.BROKERS_STEP.NO_MATCH')}
              </div>
            )}
            {filtered.map((broker) => {
              const checked = checkedIds.has(broker.id);
              return (
                <div
                  key={broker.id}
                  className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50"
                >
                  {/* Zone cliquable (icône + texte) séparée de la case : la case garde son
                      onChange, le texte son onClick → pas de double déclenchement (fix Edge). */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggle(broker.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle(broker.id);
                      }
                    }}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-3"
                  >
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
                      <Building2 size={16} />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium text-gray-900">
                        {broker.name}
                      </span>
                      {broker.email && (
                        <span className="truncate text-xs text-gray-500">{broker.email}</span>
                      )}
                    </div>
                  </div>
                  <Checkbox checked={checked} onChange={() => toggle(broker.id)} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);

BrokersSelector.displayName = 'BrokersSelector';
